import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { auth } from './firebase';
import { onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import Dashboard from './components/Dashboard';
import ClientSetup from './components/ClientSetup';
import Notes from './components/Notes';
import Prompts from './components/Prompts';
import CustomProjects from './components/CustomProjects';
import Clients from './components/Clients';
import ClientProfile from './components/ClientProfile';
import ContentQueue from './components/ContentQueue';
import SocialContent from './components/SocialContent';
import PublishingStatus from './components/PublishingStatus';
import KeywordFollowups from './components/KeywordFollowups';
import PhotoLibrary from './components/PhotoLibrary';
import ProjectBoard from './components/ProjectBoard';
import Residuals from './components/Residuals';
import Hosting from './components/Hosting';
import DashboardAdmin from './components/DashboardAdmin';
import GridTracker from './components/GridTracker';
import WhatConverts from './components/WhatConverts';
import { isStaffUser } from './services/staffAccess';
import './App.css';

const provider = new GoogleAuthProvider();

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [unreadNotes, setUnreadNotes] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [masterOpen, setMasterOpen] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  const handleGoogleLogin = async () => {
    setLoginLoading(true);
    setLoginError('');
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      setLoginError('Sign-in failed. Please try again.');
    }
    setLoginLoading(false);
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  if (authLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner-lg"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <div className="login-logo">
            <img src="/logo.png" alt="Tiana's Project Board" onError={e => e.target.style.display='none'} />
            <div className="login-title">
              <span className="login-title-top">TIANA'S</span>
              <span className="login-title-bottom">PROJECT BOARD</span>
            </div>
          </div>
          <div className="login-form">
            {loginError && <div className="login-error">{loginError}</div>}
            <button className="btn-google" onClick={handleGoogleLogin} disabled={loginLoading}>
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 6.293C4.672 4.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              {loginLoading ? 'Signing in...' : 'Sign in with Google'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isStaffUser(user)) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <div className="login-logo">
            <img src="/logo.png" alt="Tiana's Project Board" onError={e => e.target.style.display='none'} />
            <div className="login-title">
              <span className="login-title-top">TIANA'S</span>
              <span className="login-title-bottom">PROJECT BOARD</span>
            </div>
          </div>
          <div className="login-form">
            <div className="login-noaccess-title">This Google account doesn't have access</div>
            <div className="login-noaccess-text">
              You're signed in as <span className="login-noaccess-email">{user.email || 'an account with no email'}</span>.
              {' '}Sign out and use your Core6 staff account.
            </div>
            <button className="btn-login" onClick={handleLogout}>Sign out</button>
          </div>
        </div>
      </div>
    );
  }

  const navItems = [
    { path: '/',               label: 'Dashboard',       end: true },
    { path: '/clients',        label: 'Clients' },
    { path: '/social',         label: 'Social Content' },
    { path: '/prompts',        label: 'Prompts' },
    { path: '/custom-projects',label: 'Custom Projects' },
    { path: '/notes',          label: 'Notes',           badge: unreadNotes },
  ];
  const masterItems = [
    { path: '/content',           label: 'Content' },
    { path: '/photo-library',     label: 'Photos' },
    { path: '/keyword-followups', label: 'Follow-Ups' },
    { path: '/whatconverts',      label: 'WhatConverts' },
    { path: '/publishing',        label: 'Publishing' },
    { path: '/dashboards',        label: 'Client Dashboards' },
    { path: '/residuals',         label: 'Residuals' },
    { path: '/hosting',           label: 'Hosting' },
  ];
  const masterActive = masterItems.some(item => location.pathname.startsWith(item.path));

  return (
    <div className="app">
      <nav className="navbar">
        <Link to="/" className="navbar-logo" style={{ textDecoration: 'none' }} onClick={() => setMenuOpen(false)}>
          <img src="/logo.png" alt="logo" className="nav-logo-img" onError={e => e.target.style.display='none'} />
          <div className="nav-logo-text">
            <span className="nav-logo-top">TIANA'S</span>
            <span className="nav-logo-bottom">PROJECT BOARD</span>
          </div>
        </Link>
        <button className="hamburger" onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
          <span></span><span></span><span></span>
        </button>
        <div className={`navbar-links ${menuOpen ? 'open' : ''}`}>
          {navItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
              onClick={() => { setMenuOpen(false); setMasterOpen(false); }}
            >
              {item.label}
              {item.badge > 0 && <span className="nav-badge">{item.badge}</span>}
            </NavLink>
          ))}
          <div className="nav-dropdown">
            <button
              className={`nav-link${masterActive ? ' active' : ''}`}
              onClick={() => setMasterOpen(o => !o)}
            >
              Master {masterOpen ? '▴' : '▾'}
            </button>
            {masterOpen && (
              <>
                <div className="nav-dropdown-backdrop" onClick={() => setMasterOpen(false)} />
                <div className="nav-dropdown-menu">
                  {masterItems.map(item => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                      onClick={() => { setMenuOpen(false); setMasterOpen(false); }}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </nav>
      <main className="main-content">
        <Routes>
          <Route path="/"               element={<Dashboard />} />
          <Route path="/client-setup"   element={<ClientSetup />} />
          <Route path="/notes"          element={<Notes user={user} setUnreadNotes={setUnreadNotes} />} />
          <Route path="/prompts"        element={<Prompts />} />
          <Route path="/custom-projects" element={<CustomProjects />} />
          <Route path="/clients"        element={<Clients />} />
          <Route path="/clients/:id"    element={<ClientProfile />} />
          <Route path="/content"        element={<ContentQueue />} />
          <Route path="/social"         element={<SocialContent />} />
          <Route path="/publishing"     element={<PublishingStatus />} />
          <Route path="/keyword-followups" element={<KeywordFollowups />} />
          <Route path="/photo-library"  element={<PhotoLibrary />} />
          {/* WhatConverts — internal lead dashboard, clarity_enabled clients only */}
          <Route path="/whatconverts"   element={<WhatConverts />} />
          {/* Client Dashboards — staff overview of every client's dashboard: users, last login, live month, View as client */}
          <Route path="/dashboards"     element={<DashboardAdmin />} />
          {/* Owner-only (PIN + Firestore rules) — under the Master nav dropdown. */}
          <Route path="/residuals"      element={<Residuals />} />
          <Route path="/hosting"        element={<Hosting />} />
          {/* Grid Rank tracker — CANCELLED 2026-07-23: unlinked from nav + weekly
              scheduler paused, but route/component kept so the code stays reachable
              (direct URL) if it's ever revived. */}
          <Route path="/grid-tracker"   element={<GridTracker />} />
          <Route path="/projects/website-design" element={<ProjectBoard board="website" />} />
          <Route path="/projects/in-house-seo"   element={<ProjectBoard board="in-house" />} />
          <Route path="*"               element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <footer className="footer">
        <NavLink
          to="/projects/website-design"
          className={({ isActive }) => `footer-link${isActive ? ' active' : ''}`}
          onClick={() => setMenuOpen(false)}
        >
          Website Design
        </NavLink>
        <span className="footer-sep">·</span>
        <NavLink
          to="/projects/in-house-seo"
          className={({ isActive }) => `footer-link${isActive ? ' active' : ''}`}
          onClick={() => setMenuOpen(false)}
        >
          In-house SEO
        </NavLink>
        <span className="footer-sep">·</span>
        <NavLink
          to="/client-setup"
          className={({ isActive }) => `footer-link${isActive ? ' active' : ''}`}
          onClick={() => setMenuOpen(false)}
        >
          Client Setup
        </NavLink>
        <span className="footer-sep">·</span>
        <button className="btn-logout" onClick={handleLogout}>Logout</button>
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
